/**
 * Cookie Consent Banner — bottom-sheet, mobile-first.
 *
 * Compliance behaviour:
 *   • GDPR Art. 7(1): proof of consent — timestamp + version stored.
 *   • GDPR Art. 7(3) / EDPB 05/2020: withdrawing consent is as easy as giving it
 *     (same button prominence, both reachable without extra clicks).
 *   • CNIL (France) 2022 ruling vs Google: "Reject" button has EQUAL visual
 *     weight to "Accept" — same size, same colour, same font weight.
 *   • ePrivacy Art. 5(3): no non-essential storage before explicit consent.
 *   • CCPA/CPRA §1798.135(b) + Colorado CPA §6-1-1313(2)(a) + Connecticut CTDPA:
 *     Global Privacy Control (GPC) is honoured as a legally valid opt-out
 *     signal — if `navigator.globalPrivacyControl === true` we silently
 *     save "essential only" consent on first visit and never show the banner.
 *     The user can still manually opt back in via the Cookie Policy page
 *     (CPRA Reg §7025(c)(3) permits express opt-in overriding GPC).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { grantAnalyticsConsent, denyAnalyticsConsent } from './GoogleAnalytics';

const STORAGE_KEY = 'cookie_consent';

function saveChoice(choice, source = 'banner') {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...choice,
      source, // 'banner' | 'gpc-signal' | 'cookie-policy-page'
      timestamp: new Date().toISOString(),
      version: 2, // bumped when consent terms materially change
    }));
  } catch (_) { /* noop */ }
}

function hasGpcSignal() {
  try {
    return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
  } catch (_) {
    return false;
  }
}

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [gpcActive, setGpcActive] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const gpc = hasGpcSignal();
      setGpcActive(gpc);

      if (!stored) {
        if (gpc) {
          // GPC = legally-valid opt-out → silently save essential-only, never show the banner.
          saveChoice({ analytics: false }, 'gpc-signal');
          denyAnalyticsConsent();
          return;
        }
        setVisible(true);
      }
    } catch (_) {
      setVisible(true);
    }

    const openHandler = () => setVisible(true);
    window.addEventListener('open-cookie-settings', openHandler);
    return () => window.removeEventListener('open-cookie-settings', openHandler);
  }, []);

  const handleAcceptAll = () => {
    saveChoice({ analytics: true }, 'banner');
    grantAnalyticsConsent();
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveChoice({ analytics: false }, 'banner');
    denyAnalyticsConsent();
    setVisible(false);
  };

  if (!visible) return null;

  // Buttons share: same height (h-11), same width on mobile (flex-1), same font
  // weight (font-semibold), same padding, same radius, same transition. The only
  // difference is colour — Reject uses slate so it isn't the "scary" option, and
  // Accept uses the brand navy. Neither is more prominent than the other.
  const buttonBase = 'h-11 flex-1 sm:flex-none sm:min-w-[160px] rounded-md px-5 text-sm font-semibold transition-opacity hover:opacity-90';
  const buttonAccept = `${buttonBase} text-white`;
  const buttonReject = `${buttonBase} text-white`;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 pointer-events-none"
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      data-testid="cookie-consent-banner"
    >
      <div
        className="pointer-events-auto mx-auto max-w-3xl rounded-xl shadow-2xl border"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#1F3C4F' }}
      >
        <div className="p-5 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-1">
            Your privacy, your choice
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            We use strictly necessary cookies to make this site work. With your permission we would
            also like to use Google Analytics to understand how you use the site and improve it.
            No analytics cookies are set and no analytics scripts load until you click{' '}
            <strong>Accept all</strong>. See our{' '}
            <Link to="/cookie-policy" className="underline text-slate-900 hover:text-black">
              Cookie Policy
            </Link>{' '}
            for details. You can change your mind any time.
          </p>

          {gpcActive && (
            <p className="mt-3 text-xs text-slate-500" data-testid="gpc-notice">
              Your browser is sending a <strong>Global Privacy Control</strong> signal.
            </p>
          )}

          <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={handleRejectAll}
              data-testid="cookie-reject-btn"
              className={buttonReject}
              style={{ backgroundColor: '#64748B' /* slate-500 */ }}
            >
              Reject all
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              data-testid="cookie-accept-btn"
              className={buttonAccept}
              style={{ backgroundColor: '#1F3C4F' /* brand navy */ }}
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
